import {
  Alert,
  Datagrid,
  Modal,
  Button,
  Stack,
  Title,
  Spinner
} from '@efast_public/fjd-component-library';
import { useUpdateCase } from '../../api/hooks/useUpdateCase';
import { FormatUtil } from '../../shared/utils/formatUtil';
import {
  AttachmentExtendedModel,
  CaseDetailsModel
} from '../../api/models/Case/CaseDetailsModel';
import { useQuery } from '@tanstack/react-query';
import { AxiosError } from 'axios';
import { BehApiRoutes } from '../../api/beh-api';
import styles from '../../pages/Schreibtisch/SchreibtischBody/Dokumente/dokumente.module.css';
import { Bescheidstatus } from '../../api/models/Status/Bescheidstatus';
import BescheidstatusBadge from '../../pages/shared/BescheidstatusBadge/BescheidstatusBadge';
import AntragBadge from '../../pages/shared/AntragBadge/AntragBadge';
import { formatByte } from '../../shared/utils/antragsuebersicht';

interface AntragAbschliessenModalProps {
  readonly onClose: () => void;
  readonly caseId: string;
  readonly auftragsnummer: string;
  readonly version: string;
  readonly isOpen: boolean;
}

function AntragAbschliessenModal({
  onClose,
  caseId,
  auftragsnummer,
  version,
  isOpen
}: AntragAbschliessenModalProps) {
  const { downloadSummary, updateCase, loadingUpdateCase } = useUpdateCase();
  const { data: caseDetails, error: caseDetailsError } = useQuery<
    CaseDetailsModel,
    AxiosError
  >({
    queryKey: [BehApiRoutes.case(caseId)]
  });
  const {
    data: attachments,
    isLoading,
    error: attachmentsError
  } = useQuery<Array<AttachmentExtendedModel>, AxiosError>({
    queryKey: [BehApiRoutes.caseAttachments(caseId)]
  });
  const BEARBEITUNGSSTAND_KEY = 'bearbeitungsstand';

  const hasError = caseDetailsError || attachmentsError;
  const isLoading2 = loadingUpdateCase;

  const handlePrimary = async () => {
    await updateCase({
      caseId,
      updatedData: {
        caseVersion: version,
        [BEARBEITUNGSSTAND_KEY]: 'Abgeschlossen'
      }
    })
      .then((caseDetailsModel: CaseDetailsModel | undefined) => {
        if (caseDetailsModel) {
          const timestamp: string = FormatUtil.fileDateTimeFormatted();
          downloadSummary({
            caseId,
            filename: `${auftragsnummer}_${timestamp}_Download.zip`
          });
          onClose();
        }
      })
      .catch(() => {});
  };

  const initialSubmissionId = caseDetails?.initialSubmission.submissionId;

  const documents = attachments
    ? attachments.map((document) => {
        return { ...document, isBescheid: document.isBescheid };
      })
    : [];

  return (
    <Modal
      appElement={document.querySelector('.fjd-base-layout') as HTMLElement}
      closeOnBackdropClick={false}
      open={isOpen}
      windowSize={'l'}
      onClose={onClose}
    >
      <Modal.Header id="AntragAbschliessenModal-header">
        Antrag herunterladen
      </Modal.Header>
      <Modal.Content>
        <Stack direction="column">
          <Alert
            variant={'warning'}
            message={
              'Abgeschlossene Anträge werden nach 30 Tagen aus dem Behörden-Client gelöscht, auch wenn sie noch nicht heruntergeladen wurden.'
            }
          />
          <Title level={3}>
            Laden Sie den Antrag vollständig herunter, um ihn vorschriftsmäßig
            abzulegen
          </Title>
          <div>Der Download beinhaltet folgende Dokumente:</div>
          <Datagrid<(typeof documents)[number]>
            columns={[
              {
                id: 'name',
                header: 'Name',
                minSize: 800,
                accessorKey: 'description',
                enableHiding: false,
                enableSorting: true,
                enableResizing: true,
                sortingFn: 'auto',
                cell: (row) => (
                  <Stack
                    direction="row"
                    spacing="xl"
                    className={styles.container}
                  >
                    <div style={{ whiteSpace: 'initial' }}>
                      {row.row.original.description + row.row.original.filetype}
                    </div>
                    {row.row.original.isBescheid &&
                      (caseDetails?.bescheidstatus === Bescheidstatus.POSITIV ||
                        caseDetails?.bescheidstatus ===
                          Bescheidstatus.NEGATIV) && (
                        <BescheidstatusBadge
                          bescheidstatus={caseDetails?.bescheidstatus}
                          fjdBadgeSize={'xs'}
                          badgeLabel={'Bescheid'}
                        />
                      )}
                    {row.row.original.submissionId === initialSubmissionId && (
                      <AntragBadge data-testid="antragBadge" />
                    )}
                  </Stack>
                )
              },
              {
                id: 'filetype',
                header: 'Typ',
                accessorKey: 'filetype',
                enableHiding: false,
                size: 100,
                minSize: 80,
                enableSorting: true,
                enableResizing: true,
                sortingFn: 'auto',
                cell: (row) => (
                  <span>{row.getValue<string>().toUpperCase()}</span>
                )
              },
              {
                id: 'size',
                header: 'Größe',
                accessorKey: 'size',
                enableHiding: false,
                size: 100,
                minSize: 80,
                enableSorting: true,
                enableResizing: true,
                sortingFn: 'auto',
                cell: (row) => (
                  <span>{formatByte(`${row.getValue<string>()}`)}</span>
                )
              }
            ]}
            data={documents}
            dataKey="id"
            isDataLoading={isLoading}
            multiSelect={false}
            areColumnsHideable={false}
            areColumnsResizeable
            manualPagination={false}
            manualSorting={false}
            labels={{
              currentPage: 'Aktuelle Seite',
              detailButton: 'Weitere Infos ein-/ausblenden',
              emptyData: 'Keine Dokumente gefunden.',
              nextPage: 'Nächste Seite',
              of: 'Seite {0} von {1}',
              page: 'Seite',
              pageSize: '{0} Einträge pro Seite',
              previousPage: 'Vorherige Seite',
              rows: 'Zeilen',
              tableTitle: '',
              ascBtn: 'Aufsteigend sortieren',
              descBtn: 'Absteigend sortieren',
              hideBtn: 'Spalte ausblenden'
            }}
          />
        </Stack>
      </Modal.Content>
      <Modal.Footer>
        {hasError ? (
          <Button variant="primary" onClick={onClose}>
            Schließen
          </Button>
        ) : (
          <>
            <Button
              variant="secondary-outline"
              disabled={isLoading2}
              onClick={onClose}
            >
              Abbrechen
            </Button>
            <Button
              variant="primary"
              disabled={isLoading2}
              onClick={handlePrimary}
            >
              Antrag abschließen und herunterladen (ZIP)
            </Button>
          </>
        )}
      </Modal.Footer>
    </Modal>
  );
}

export default AntragAbschliessenModal;
